import { TaskLinkEntity } from './task.props.type';

import * as Enumerable from 'linq';
import { DailyPhaseName, TaskGroupBy, TaskLinkType, TaskPhaseName, TaskStatus, TaskTarget } from './task.enum';
import { getEntityTaskLink, getPhaseTag } from './task.utility';
import { NestedTask, Task } from './task';
import { v4 } from 'uuid';
import { LabelEntity, LabelType } from '../shared';
import { TaskViewOptions, defaultViewOptions } from './task.options';
import { ContactEntity } from '../contact';
import { FiltersRelativeDateUtility, DateUtility } from '../../utilities'

export class TaskGroupUtility {

    public static noLocation = "No Location";

    public static createNestedTask(task: Task, currentGroupBy: TaskGroupBy): NestedTask {

        const groupLinks: Record<TaskLinkType, TaskLinkEntity> = {} as any;

        if (task.assignee) {
            groupLinks[TaskLinkType.Assignee] = getEntityTaskLink({ ...task.assignee, data: task.assignee?.name ?? "" }, TaskLinkType.Assignee);
        }

        if (task?.data?.location) {
            const location = task.data.location.toUpperCase();
            groupLinks[TaskLinkType.Location] = getEntityTaskLink({ _id: location, name: location, data: location }, TaskLinkType.Location);
        }

        if (task.target) {
            groupLinks[TaskLinkType.Target] = getEntityTaskLink({ _id: task.target, name: task.target, data: task.target }, TaskLinkType.Type);
        }

        if (task.type) {
            groupLinks[TaskLinkType.Type] = getEntityTaskLink({ _id: task.type, name: task.type, data: task.type }, TaskLinkType.Type);
        }

        if (task.status) {
            groupLinks[TaskLinkType.Status] = getEntityTaskLink({ _id: task.status, name: task.status, data: task.status }, TaskLinkType.Type);
        }
        else {
            groupLinks[TaskLinkType.Status] = getEntityTaskLink({ _id: TaskStatus.Open, name: TaskStatus.Open, data: TaskStatus.Open }, TaskLinkType.Type);
        }

        Enumerable
            .from(task.links as TaskLinkEntity[])
            .forEach(x => {
                groupLinks[x.type] = x;
            });

        Enumerable
            .from(task.tags)
            .forEach(x => {

                switch (x.type) {
                    case LabelType.Phase:
                        groupLinks[x._id] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType.Phase);
                        groupLinks[TaskLinkType.Phase] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType.Phase);
                        return;

                    case LabelType.File:
                        groupLinks[x._id] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType.Tags);
                        return;

                    default:
                        groupLinks[x.type] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType[x.type]);
                }
            });

        const newTask = {
            ...task,
            groupLinks,
            children: [],
            currentGroupBy: currentGroupBy,
        } as NestedTask;

        return newTask;
    }

    public static build(tasks: Task[], options: TaskViewOptions, includesDailyGroup: boolean = true, linkLevel?: TaskLinkEntity): NestedTask[] {

        const safeOptions = options ?? defaultViewOptions;
        let result: NestedTask[];

        const start = new Date();
        //console.log((new Date()).getTime() - start.getTime());

        switch (safeOptions.groupBy) {
            case TaskGroupBy.Stage:
                result = this.groupByStage(tasks, safeOptions);
                break;
            case TaskGroupBy.Phase:
                result = this.groupByPhase(tasks, safeOptions, includesDailyGroup, linkLevel);
                break;
            case TaskGroupBy.Assignee:
                result = this.groupByAssignee(tasks, safeOptions);
                break;
            case TaskGroupBy.Tags:
                result = this.groupByTags(tasks, safeOptions);
                break;
            case TaskGroupBy.Location:
                result = this.groupByField(TaskLinkType.Location, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Location, this.noLocation, this.noLocation), null);
                break;
            case TaskGroupBy.Target:
                const targetMap = new Map<string, NestedTask>([TaskTarget.Field, TaskTarget.Office, TaskTarget.Supervisor].map(e => [e, null]));
                result = this.groupByField(TaskLinkType.Target, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Target, "No Target", "No Target"), targetMap);
                break;
            case TaskGroupBy.Type:
                result = this.groupByField(TaskLinkType.Type, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Type, "No Type", "No Type"), null);
                break;
            case TaskGroupBy.Status:
                const statusMap = new Map<string, NestedTask>([TaskStatus.Open, TaskStatus.Completed].map(e => [e, null]));
                result = this.groupByField(TaskLinkType.Status, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Status, "No Status", "No Status"), statusMap);
                break;
            default:
                result = this.groupByNone(tasks, safeOptions);
                break;
        }

        //console.log((new Date()).getTime() - start.getTime());
        if (!safeOptions.showEmptyGroups) {

            result = Enumerable
                .from(result)
                .where(e => {

                    if (e.type === TaskLinkType.Group) {

                        if ([DailyPhaseName.CheckIn, DailyPhaseName.CheckOut].includes(e._id as any)) {
                            // We are dealing with the daily task groups adn we need to check the children have children
                            e.children = e.children.filter(x => x.children.length > 0);
                        }

                        return e.children.length > 0;
                    }

                    return true;

                })
                .toArray();
        }


        return result;
    }

    private static groupByStage(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        let rootParents = [];
        const taskMap = new Map(tasks.map(e => [e._id, this.createNestedTask(e, TaskGroupBy.Stage)]))
        const parents = new Map<string, NestedTask>(tasks.filter(e => !!e.parent).map(e => [e.parent._id, taskMap.get(e.parent._id)]));

        for (const task of taskMap.values()) {
            if (!task.parent) {
                rootParents.push(task);
                continue;
            }

            let parent = parents.get(task.parent._id);
            if (!parent) {
                continue;
            }

            if (!parent?.children) {
                parent.children = [];
            }

            parent.children.push(task);
        }

        this.filterAndSortChildInParents([...Array.from(parents.values()), ...Array.from(parents.values())], options);

        const groups: NestedTask[] = [];

        if (options.sortBy === TaskGroupBy.Stage) {
            rootParents = this.sortByStage(rootParents, options);
        } else {
            rootParents = this.sortBySortOrder(rootParents, options);
        }

        for (const parent of rootParents) {
            const id = parent._id;
            if (!taskMap.has(id)) {
                continue;
            }

            groups.push({
                ...taskMap.get(id),
                currentGroupBy: TaskGroupBy.Stage
            });
        }

        return groups;
    }

    private static groupByPhase(tasks: Task[], options: TaskViewOptions, includesDailyGroup: boolean = true, linkLevel: TaskLinkEntity): NestedTask[] {

        const addDailyToStage = linkLevel?.type === TaskLinkType.Job
        const virtualStageTasks: Map<string, NestedTask> = new Map<string, NestedTask>();

        let enumerable = Enumerable.empty<NestedTask>();
        if (addDailyToStage && includesDailyGroup) {
            // create stage tasks in daily group
            enumerable = Enumerable.from(tasks).where(x => x.type !== TaskLinkType.Job)
                .selectMany(e => {
                    if (e.type === TaskLinkType.WorkOrder) {
                        const stagesInDaily = [DailyPhaseName.CheckIn, DailyPhaseName.CheckOut].map(x => {
                            const fake = this.createNestedTask(e, TaskGroupBy.Phase)
                            fake._id = `${e._id}+${x}`;
                            fake.children = [];
                            fake.parent = {
                                _id: x,
                                name: x,
                                type: TaskLinkType.Phase
                            }
                            return fake
                        });

                        for (const item of stagesInDaily) {
                            virtualStageTasks.set(item._id, item);
                        }

                        return stagesInDaily;
                    }

                    return [this.createNestedTask(e, TaskGroupBy.Phase)];
                });
        }
        else {
            enumerable = Enumerable.from(tasks).where(x => !x.virtual).select(e => this.createNestedTask(e, TaskGroupBy.Phase));
        }

        const taskList = enumerable.toArray();
        const noPhaseTasks = this.createGroupTask(TaskGroupBy.Phase, "No Phase", "No Phase");
        const parents = new Map<string, NestedTask>(taskList.map(e => [e._id, e]));
        const phaseName: string[] = [TaskPhaseName.Before, TaskPhaseName.During, TaskPhaseName.After];

        if (includesDailyGroup) {
            phaseName.push(DailyPhaseName.CheckIn, DailyPhaseName.CheckOut);
        }

        const phases = new Map<string, NestedTask>(phaseName
            .map(e => {
                const phase = this.createGroupTask(TaskGroupBy.Phase, e, e, getPhaseTag(e), null);
                parents.set(e, phase);
                return [e, phase];
            }));

        const foundParents = new Map<string, NestedTask>();

        for (const task of taskList) {

            let parent = parents.get(task.parent._id);
            if (parent) {
                if (!parent?.children) {
                    parent.children = [];
                }

                parent.children.push(task);
                if (task.type === TaskLinkType.Compliance) {
                    parent.hasComplianceSubtask = true;
                }

                if (!foundParents.has(parent._id)) {
                    foundParents.set(parent._id, parent);
                }

                continue;
            }

            let inGroup = false;
            for (const key of phases.keys()) {
                const tag = task.groupLinks[key];
                if (!tag) {
                    continue;
                }

                inGroup = true;
                const group = phases.get(key)

                if (virtualStageTasks.size > 0 && (group._id === DailyPhaseName.CheckIn || group._id === DailyPhaseName.CheckOut)) {
                    const found = virtualStageTasks.get(`${task.parent._id}+${group._id}`);
                    if (found) {
                        found.children.push(task);
                        continue;
                    }
                }

                // Set the group by as the parent for the task. this is use on field app
                task.virtualParent = { ...group, children: [] };
                group.children.push(task);
            }

            if (!inGroup) {
                task.virtualParent = { ...noPhaseTasks, children: [] };
                noPhaseTasks.children.push(task);
            }
        }

        this.filterAndSortChildInParents([...(Array.from(foundParents.values()))], options);

        if (noPhaseTasks.children.length === 0) {
            return Array.from(phases.values());
        }

        return [...Array.from(phases.values()), noPhaseTasks];
    }

    private static groupByAssignee(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        let assignees: NestedTask[] = [];
        const noVirtual = Enumerable
            .from(tasks)
            .where(x => {
                if (x.daily) {
                    return x.instance;
                }

                return !x.virtual
            })
            .select(e => {
                if (e.assignee) {
                    assignees.push(this.createGroupTask(TaskGroupBy.Assignee, e.assignee.name, e.assignee._id, null, e.assignee));
                }

                return this.createNestedTask(e, TaskGroupBy.Assignee)
            })
            .toArray();

        const enumerable = Enumerable.from(assignees).distinct(e => e._id);
        if (options.sortOrder === 'asc') {
            assignees = enumerable.orderBy(e => e.name).toArray();
        }
        else {
            assignees = enumerable.orderByDescending(e => e.name).toArray();
        }

        const assigneeMap = new Map<string, NestedTask>(assignees.map(e => [e._id, e]));
        const noAssigneeTasks = this.createGroupTask(TaskGroupBy.Assignee, "No Assignee", "No Assignee");
        const parents = new Map<string, NestedTask>(noVirtual.map(e => [e._id, e]));

        for (const task of noVirtual) {

            const foundParent = parents.get(task.parent._id);
            if (foundParent) {
                task.fullParent = foundParent;
            }

            const assignee = task.groupLinks[TaskLinkType.Assignee]?._id;
            const found = assigneeMap.get(assignee);
            if (found) {
                found.children.push(task);
            }
            else {
                noAssigneeTasks.children.push(task);
            }
        }

        let groups: NestedTask[];
        if (noAssigneeTasks.children.length === 0) {
            groups = Array.from(assigneeMap.values());
        }
        else {
            groups = [...Array.from(assigneeMap.values()), noAssigneeTasks];
        }

        this.filterAndSortChildInParents(groups, options);

        return groups.filter(e => !!e.children?.length);
    }

    private static groupByNone(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        const noVirtual = Enumerable
            .from(tasks)
            .where(x => !x.virtual)
            .select(e => this.createNestedTask(e, TaskGroupBy.None))
            .toArray();

        const taskMap = new Map<string, NestedTask>(noVirtual.map(e => [e._id, e]));
        const foundParent = new Map<string, NestedTask>();

        for (const task of noVirtual) {

            let parent = taskMap.get(task.parent._id);

            if (!parent) {
                continue;
            }

            taskMap.delete(task._id);

            if (!parent?.children) {
                parent.children = [];
            }

            parent.children.push(task);

            if (!foundParent.has(parent._id)) {
                foundParent.set(parent._id, parent);
            }
        }

        const temp = { children: Array.from(taskMap.values()) } as NestedTask;
        this.filterAndSortChildInParents([...Array.from(foundParent.values()), temp], options);

        const items = temp.children;;

        return items;
    }

    private static groupByTags(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        const tagGroups = new Map<string, NestedTask>();

        const noVirtual = Enumerable
            .from(tasks)
            .where(x => {
                if (x.daily) {
                    return x.instance;
                }

                return !x.virtual
            })
            .select(e => {
                const nested = this.createNestedTask(e, TaskGroupBy.Tags);

                if (e.tags?.length > 0) {
                    for (const tag of e.tags) {
                        if (tag.type !== LabelType.File || tagGroups.has(tag._id)) {
                            continue;
                        }

                        tagGroups.set(tag._id, this.createGroupTask(TaskGroupBy.Tags, tag.name, tag.name, tag, null));
                    }
                }

                return nested;
            })
            .toArray();

        const noTagTasks = this.createGroupTask(TaskGroupBy.Tags, "No Tag", "No Tag",);
        const parents = new Map(noVirtual.map(e => [e._id, e]));

        for (const task of noVirtual) {

            const foundParent = parents.get(task.parent._id);
            if (foundParent) {
                task.fullParent = foundParent;
            }

            let inGroup = false;
            for (const key of tagGroups.keys()) {
                const tag = task.groupLinks[key];
                if (!tag) {
                    continue;
                }

                inGroup = true;
                const group = tagGroups.get(key)
                group.children.push(task);
            }

            if (!inGroup) {
                noTagTasks.children.push(task);
            }
        }

        let groups: NestedTask[];

        if (noTagTasks.children.length === 0) {
            groups = Array.from(tagGroups.values());
        }
        else {
            groups = [...Array.from(tagGroups.values()), noTagTasks];
        }

        this.filterAndSortChildInParents(groups, options);


        return groups;
    }

    private static groupByField(groupByField: TaskLinkType, tasks: Task[], options: TaskViewOptions, emptyGroup: NestedTask, groupByFieldMap: Map<string, NestedTask>): NestedTask[] {
        if (!groupByFieldMap) {
            groupByFieldMap = new Map<string, NestedTask>();
        }

        const noVirtual = Enumerable
            .from(tasks)
            .where(x => !x.virtual)
            .select(e => {
                return this.createNestedTask(e, TaskGroupBy[groupByField])
            })
            .select(e => {
                const field = e.groupLinks[groupByField];

                if (field && !groupByFieldMap.get(field._id)) {

                    const fieldTask =
                        {
                            ...field,
                            children: [],
                            type: TaskLinkType.Group,
                            currentGroupBy: TaskGroupBy[groupByField]
                        } as NestedTask;

                    if (groupByField === TaskLinkType.Target) {
                        fieldTask['target'] = TaskTarget[field._id];
                    }

                    groupByFieldMap.set(field._id, fieldTask)
                }

                return e;
            })
            .toArray();

        const parents = new Map<string, NestedTask>(noVirtual.map(e => [e._id, e]));
        const foundParents = new Map<string, NestedTask>();

        for (const task of noVirtual) {
            let parent = parents.get(task.parent._id);
            if (parent) {
                parents.delete(task._id);

                if (!parent?.children) {
                    parent.children = [];
                }

                parent.children.push(task);
                if (!foundParents.has(parent._id)) {
                    foundParents.set(parent._id, parent);
                }
                continue;
            }

            const value = task.groupLinks[groupByField]?._id;
            const found = groupByFieldMap.get(value);
            if (found) {
                found.children.push(task);
            }
            else {
                emptyGroup.children.push(task);
            }
        }

        const groups = [...(Array.from(groupByFieldMap.values())).filter(e => !!e?.children?.length), emptyGroup];
        this.filterAndSortChildInParents([...(Array.from(foundParents.values())), ...groups], options);

        return groups.filter(e => !!e.children?.length);
    }

    private static createGroupTask(currentGroupBy: TaskGroupBy, name: string, id: string = null, tag: LabelEntity = null, assignee: ContactEntity = null): NestedTask {
        return {
            name: name,
            _id: id ?? v4(),
            type: TaskLinkType.Group,
            tags: !tag ? null : [tag],
            assignee: assignee,
            children: [],
            currentGroupBy: currentGroupBy
        } as NestedTask;
    }

    private static filterAndSortChildInParents(parents: NestedTask[], taskViewOptions: TaskViewOptions) {
        if (!parents?.length) {
            return [];
        }

        let sortField = TaskLinkType[taskViewOptions.sortBy];
        if (taskViewOptions.sortBy === TaskGroupBy.None) {
            sortField = 'sortOrder';
        }

        for (const parent of parents) {
            if (!parent?.children) {
                continue;
            }

            // sort work orders under check in / check out group when group by phase
            if ((parent.name === DailyPhaseName.CheckIn || parent.name === DailyPhaseName.CheckOut) && parent.currentGroupBy === TaskGroupBy.Phase) {
                const sortedWorkOrders = Enumerable
                    .from(parent.children)
                    .orderBy(e => e.sortOrder)
                    .toArray();

                parent.children = sortedWorkOrders;
                continue;
            }

            const tasks = parent.children;
            let enumerable = Enumerable.from(tasks)

            if (taskViewOptions.filterValue) {
                enumerable = enumerable.where(e => {
                    const match = e.children?.length > 0 || this.filter(e, taskViewOptions);
                    return match;
                });
            }

            // wont sort if the sort by is stage
            if (taskViewOptions.sortBy === TaskGroupBy.Stage) {
                parent.children = enumerable.toArray();
                continue;
            }

            if (taskViewOptions.sortOrder === 'asc') {
                parent.children = enumerable
                    .orderBy(e => {
                        const data = e.groupLinks[sortField]?.data ?? "";
                        return data;
                    }).toArray();
                continue;
            }

            parent.children = enumerable
                .orderByDescending(e => {
                    const data = e.groupLinks[sortField]?.data ?? "";
                    return data;
                }).toArray();
        }
    }

    private static sortByStage(parents: NestedTask[], taskViewOptions: TaskViewOptions): NestedTask[] {
        if (taskViewOptions.sortOrder === 'asc') {
            parents = Enumerable.from(parents)
                .orderBy(e => {
                    if (e.type === TaskLinkType.WorkOrder) {
                        // return e.name;
                        return e.sortOrder;
                    }

                    return ' ';
                }).toArray();
        }
        else {
            parents = Enumerable.from(parents)
                .orderByDescending(e => {
                    if (e.type === TaskLinkType.WorkOrder) {
                        // return e.name;
                        return e.sortOrder;
                    }

                    return ' ';
                }).toArray();
        }

        return parents;
    }

    private static sortBySortOrder(parents: NestedTask[], taskViewOptions: TaskViewOptions): NestedTask[] {
        if (taskViewOptions.sortOrder === 'asc') {
            parents = Enumerable
                .from(parents)
                .orderBy(e => e.sortOrder)
                .toArray();
        }
        else {
            parents = Enumerable
                .from(parents)
                .orderByDescending(e => e.sortOrder)
                .toArray();
        }

        return parents;
    }

    private static filter(task: NestedTask, taskViewOptions: TaskViewOptions): boolean {
        if (!taskViewOptions.filterValue) {
            return true;
        }

        const filterValue = taskViewOptions.filterValue;
        const result: boolean[] = [];
        for (const field in filterValue) {
            if (!filterValue[field]) {
                result.push(true);
                continue;
            }

            if (!task[field]) {
                result.push(false);
            }


            let match: boolean = true;
            switch (field) {
                case 'tags': {

                    const arr = filterValue[field] as any[];
                    if (arr.length === 0) {
                        break;
                    }

                    const arrFieldValue = task[field] as LabelEntity[];
                    match = arr.some(e => arrFieldValue.findIndex(l => l._id === e._id) > -1);
                    result.push(match);
                    break;
                }

                case 'assignee':
                case "completedBy": {
                    const arr = filterValue[field] as any[];
                    if (arr.length === 0) {
                        break;
                    }

                    match = arr.some(e => e._id === task[field]?._id);
                    result.push(match);
                    break;
                }

                case "completedOn":
                case 'dueDate':

                    const createdDateFilter = !!filterValue[field]
                        ? FiltersRelativeDateUtility.getFilter(filterValue[field]) as any
                        : null;

                    if (createdDateFilter) {
                        const dt = DateUtility.getDateTime(task[field], 'utc').toJSDate();
                        const { $gte, $lte, $lt } = createdDateFilter;

                        const isGTE = $gte
                            ? $gte && dt >= $gte
                            : true;

                        const isLTE = $lte
                            ? $lte && dt <= $lte
                            : true;

                        const isLT = $lt
                            ? $lt && dt < $lt
                            : true;

                        match = isGTE && isLTE && isLT;
                    }
                    break;

                case "daily":
                case 'answered':
                    match = task[field] === filterValue[field];
                    break;

                case 'name':
                    match = task.name?.includes(filterValue[field]);
                    break;


                case "status":
                case 'target':
                case 'type': {
                    const arr = filterValue[field] as any[];
                    if (arr.length === 0) {
                        break;
                    }

                    match = arr.some(e => e._id === task[field]);
                    break;
                }
            }

            result.push(match);
        }

        return !result.some(e => !e);
    }
}
